import { useState } from 'react';

import { useAppData } from '@/app-data';
import * as api from '@/features/backlog/api';
import { useIdentity } from '@/features/identity/identity';
import { Member, Role } from '@/features/backlog/types';
import { Button } from '@/components/ui/Button';
import { inputCls } from '@/components/ui/form';
import { cn } from '@/components/ui/cn';

const PALETTE = ['#305bab', '#e8590c', '#2b8a3e', '#9c36b5', '#0b7285', '#c2255c', '#5f3dc4', '#a5741f'];

type RowEdit = { name: string; color: string; email: string; role: Role };

/** Members admin: rename, recolor, activate/deactivate, set role, add, delete. Imported Miro
 *  people arrive as "Member NN" placeholders — rename them here. Managing members is admin-only
 *  under SSO (enforced server-side); non-admins get a read-only view. */
export default function MembersPage() {
  const { members, reloadMembers } = useAppData();
  const { me, oidc } = useIdentity();
  const canManage = !oidc || me?.role === 'admin';
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const val = (m: Member): RowEdit => edits[m.id] ?? { name: m.name, color: m.color, email: m.email ?? '', role: m.role };
  const patch = (m: Member, p: Partial<RowEdit>) => setEdits((s) => ({ ...s, [m.id]: { ...val(m), ...p } }));

  const save = async (m: Member) => {
    setBusy(true);
    try {
      await api.updateMember(m.id, { ...val(m), active: m.active });
      setEdits((s) => {
        const n = { ...s };
        delete n[m.id];
        return n;
      });
      await reloadMembers();
    } finally {
      setBusy(false);
    }
  };
  const toggleActive = async (m: Member) => {
    setBusy(true);
    try {
      await api.updateMember(m.id, { name: m.name, color: m.color, email: m.email ?? null, active: !m.active });
      await reloadMembers();
    } finally {
      setBusy(false);
    }
  };
  const del = async (m: Member) => {
    if (!window.confirm(`Delete ${m.name}? Items assigned to them keep no assignee.`)) return;
    setBusy(true);
    try {
      await api.deleteMember(m.id);
      await reloadMembers();
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      await api.createMember({ name: newName.trim(), email: newEmail.trim() || null });
      setNewName('');
      setNewEmail('');
      await reloadMembers();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-bold text-fg-strong">Members</h1>
          <p className="text-[13px] text-fg-muted">
            {canManage
              ? 'Assignable people. Rename the imported placeholders and set roles here.'
              : 'Assignable people on your team. Only admins can manage members.'}
          </p>
        </div>
      </div>

      <div className="card p-4">
        {canManage ? (
          <div className="mb-4 flex items-center gap-2">
            <input
              className={inputCls}
              placeholder="New member name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
            <input
              className={inputCls}
              type="email"
              placeholder="Email (optional)"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && add()}
            />
            <Button variant="primary" disabled={busy} onClick={add}>
              Add member
            </Button>
          </div>
        ) : null}

        <div className="space-y-2">
          {members.map((m) =>
            canManage ? (
              <ManageRow key={m.id} m={m} v={val(m)} dirty={!!edits[m.id]} busy={busy} patch={patch} save={save} toggleActive={toggleActive} del={del} />
            ) : (
              <ReadonlyRow key={m.id} m={m} />
            ),
          )}
          {members.length === 0 ? <div className="py-6 text-center text-[13px] text-fg-faint">No members yet.</div> : null}
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  if (role !== 'admin') return null;
  return (
    <span className="shrink-0 rounded bg-brand/15 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-brand">
      Admin
    </span>
  );
}

function ReadonlyRow({ m }: { m: Member }) {
  return (
    <div className={cn('flex items-center gap-3 rounded-lg px-2 py-2', !m.active && 'opacity-50')}>
      <span className="block h-6 w-6 shrink-0 rounded-full ring-1 ring-line" style={{ background: m.color }} />
      <span className="text-[13px] font-medium text-fg-strong">{m.name}</span>
      <RoleBadge role={m.role} />
      {m.email ? <span className="text-[12px] text-fg-faint">{m.email}</span> : null}
      {!m.active ? <span className="text-[11px] text-fg-ghost">(inactive)</span> : null}
    </div>
  );
}

function ManageRow({
  m,
  v,
  dirty,
  busy,
  patch,
  save,
  toggleActive,
  del,
}: {
  m: Member;
  v: RowEdit;
  dirty: boolean;
  busy: boolean;
  patch: (m: Member, p: Partial<RowEdit>) => void;
  save: (m: Member) => void;
  toggleActive: (m: Member) => void;
  del: (m: Member) => void;
}) {
  // The role select must NOT reuse inputCls (its `w-full` overrides a fixed width and, being
  // shrink-0, would then hog the whole row — collapsing the name/email inputs and pushing the
  // action buttons off-screen). Give it its own compact, fixed-width styling instead.
  const selectCls =
    'w-[104px] shrink-0 rounded-lg border border-line bg-surface-inset px-2 py-2 text-[13px] text-fg focus:border-accent/50 focus:outline-none';
  return (
    <div className={cn('flex items-center gap-2 rounded-lg px-1 py-1', !m.active && 'opacity-50')}>
      <div className="relative h-8 w-8 shrink-0">
        <span className="block h-8 w-8 rounded-full ring-1 ring-line" style={{ background: v.color }} />
        <input
          type="color"
          value={v.color}
          onChange={(e) => patch(m, { color: e.target.value })}
          className="absolute inset-0 h-8 w-8 cursor-pointer opacity-0"
          title="Pick color"
        />
      </div>
      <input
        className={cn(inputCls, 'min-w-0 flex-1')}
        placeholder="Name"
        value={v.name}
        onChange={(e) => patch(m, { name: e.target.value })}
      />
      <input
        className={cn(inputCls, 'min-w-0 flex-1')}
        type="email"
        placeholder="email@…"
        value={v.email}
        onChange={(e) => patch(m, { email: e.target.value })}
      />
      <select className={selectCls} value={v.role} onChange={(e) => patch(m, { role: e.target.value as Role })} title="Role">
        <option value="member">Member</option>
        <option value="admin">Admin</option>
      </select>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button variant="outline" size="sm" disabled={busy || !dirty} onClick={() => save(m)}>
          Save
        </Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => toggleActive(m)}>
          {m.active ? 'Deactivate' : 'Activate'}
        </Button>
        <Button variant="danger" size="sm" disabled={busy} onClick={() => del(m)}>
          Delete
        </Button>
      </div>
    </div>
  );
}
